import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { API, loginCliente } from "services/api";
import { ACCESS_TOKEN, INTREX_AUTH } from "services/authTokens";

export type User = {
  id: number | null;
  email?: string | null;
  clienteId?: number | null;
  roles?: string[];
  razaoSocial?: string | undefined;
};

export type AuthCtx = {
  loading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  login: (emailPrincipal: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

export const AuthContext = createContext<AuthCtx | null>(null);

const mapUser = (j: any): User => {
  const displayName = j?.razaoSocial ?? j?.razao_social ?? undefined;

  return {
    id: Number(j?.id ?? j?.userId ?? j?.usuarioId ?? 0) || null,
    email: j?.email ?? j?.emailPrincipal ?? null,
    clienteId: Number(j?.clienteId ?? j?.clientId ?? j?.cid ?? 0) || null,
    roles: Array.isArray(j?.roles) ? j.roles : [],
    razaoSocial: displayName,
  };
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [runtimeCsrfToken, setRuntimeCsrfToken] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(INTREX_AUTH);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (parsed?.csrfToken) {
        setRuntimeCsrfToken(parsed.csrfToken);
      }
    } catch (e) {
      console.error("Erro lendo auth do localStorage", e);
    }
  }, []);

  useEffect(() => {
    try {
      if (!user && !runtimeCsrfToken) {
        localStorage.removeItem(INTREX_AUTH);
        return;
      }

      const payload = { user, csrfToken: runtimeCsrfToken };
      localStorage.setItem(INTREX_AUTH, JSON.stringify(payload));
    } catch (e) {
      console.error("Erro salvando auth no localStorage", e);
    }
  }, [user, runtimeCsrfToken]);

  const refreshUser = useCallback(async () => {
    try {
      const resp = await API.get("/me");
      const j = resp.data?.user ?? resp.data;

      if (j && (j.id || j.userId || j.usuarioId)) {
        const mapped = mapUser(j);
        setUser(mapped);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const login = useCallback(
    async (emailPrincipal: string, senha: string) => {
      setLoading(true);
      try {
        const data = await loginCliente({ emailPrincipal, senha });

        if (data?.csrfToken) {
          setRuntimeCsrfToken(data.csrfToken);
        }

        await refreshUser();
      } finally {
        setLoading(false);
      }
    },
    [refreshUser]
  );

  const logout = useCallback(async () => {
    try {
      await API.post("/auth/logout");
    } finally {
      localStorage.removeItem(ACCESS_TOKEN);
      localStorage.removeItem(INTREX_AUTH);
      delete API.defaults.headers.common["Authorization"];

      setRuntimeCsrfToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await refreshUser();
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshUser]);

  const value = useMemo<AuthCtx>(
    () => ({
      loading,
      isAuthenticated,
      user,
      login,
      logout,
      refreshUser,
    }),
    [loading, isAuthenticated, user, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}